import { useMemo } from "react";
import { extractCascadeRounds, type CascadeRound } from "../engine/cascade";
import type { ReplayedOutcome } from "../engine/types";
import { GridPreview } from "./GridPreview";

/**
 * Spec 019 US4 (T034) — round-by-round cascade (tumble / avalanche)
 * render of one sampled outcome.
 *
 * Each `evaluate` in the spin's `state_path` is one cascade round; the
 * rounds render top-to-bottom in chain order (US4 independent test:
 * "the per-round grids render in sequence"). Per round:
 *
 * - **Winners vacate** (scenario 1): cells the round's
 *   `remove_winning_symbols` cleared (`round.removed`) render an
 *   explode/vacate marker on this round's grid — the symbols that paid
 *   and are about to drop out.
 * - **Refill drops in** (scenario 1): cells whose symbol changed versus
 *   the previous round (`round.dropped`) render a fresh-fill marker —
 *   the symbols that tumbled into the vacated cells.
 * - **Per-round multiplier** (scenario 2): the accumulating `×N` from
 *   the evaluate's `detail.multiplier`, shown in the round header and
 *   following the declared curve.
 *
 * A single-evaluation (non-cascading) spin yields exactly one round
 * with nothing vacating and no connector — the graceful degenerate
 * case (scenario 3). The panel is hidden upstream (App) for samples
 * with no tumble, so this degenerate path is exercised only when a
 * caller passes a single-round outcome directly.
 *
 * Data source: `adapter.sampleOutcomes` (the CPU raw path), the same
 * per-evaluate `state_path` the spin replay reads — the cascade adds
 * no new engine→viewer contract field (data-model §10).
 */
export function CascadePanel({
  outcome,
  spinIndex,
}: {
  outcome: ReplayedOutcome;
  spinIndex: number;
}) {
  const rounds = useMemo(() => extractCascadeRounds(outcome), [outcome]);
  const totalWin = rounds.reduce((sum, r) => sum + r.win, 0);
  const isTumble = rounds.length > 1;

  return (
    <section
      data-testid="cascade-panel"
      data-rounds={rounds.length}
      className="border border-paper-border p-4 dark:border-term-border"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
            Cascade replay
          </h2>
          <p className="text-[10px] text-paper-hint dark:text-term-hint">
            spin #{spinIndex.toLocaleString()} of the sample pass (same seed) ·{" "}
            {rounds.length.toLocaleString()} round
            {rounds.length === 1 ? "" : "s"}
          </p>
        </div>
        <span
          data-testid="cascade-total-win"
          className="text-xs tabular-nums text-paper-label dark:text-term-label"
        >
          total {totalWin.toFixed(2)}x
        </span>
      </header>

      {!isTumble && (
        <p
          data-testid="cascade-no-tumble"
          className="mb-2 text-[10px] text-paper-hint dark:text-term-hint"
        >
          no tumble — single evaluation, no winning symbols cascaded.
        </p>
      )}

      <ol className="space-y-0">
        {rounds.map((round, i) => (
          <li key={round.index}>
            <CascadeRoundView round={round} />
            {i < rounds.length - 1 && <TumbleConnector />}
          </li>
        ))}
      </ol>

      {isTumble && (
        <p className="mt-3 text-[10px] text-paper-hint dark:text-term-hint">
          💥 winning symbols vacate · ✦ refill dropped in · ×N = accumulating
          cascade multiplier
        </p>
      )}
    </section>
  );
}

function CascadeRoundView({ round }: { round: CascadeRound }) {
  return (
    <div data-testid={`cascade-round-${round.index}`} className="py-1">
      <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-paper-hint dark:text-term-hint">
        <span className="font-bold text-paper-label dark:text-term-label">
          Round {round.index + 1}
        </span>
        <span data-testid={`cascade-round-${round.index}-multiplier`}>
          ×
          <span className="tabular-nums text-paper-label dark:text-term-label">
            {round.multiplier}
          </span>
        </span>
        <span data-testid={`cascade-round-${round.index}-win`}>
          win{" "}
          <span className="tabular-nums text-paper-label dark:text-term-label">
            {round.win.toFixed(2)}x
          </span>
        </span>
        {round.removed.length > 0 && (
          <span
            data-testid={`cascade-round-${round.index}-removed`}
            className="text-rose-700 dark:text-rose-400"
          >
            {round.removed.length} vacating
          </span>
        )}
      </div>
      <GridPreview
        grid={round.grid}
        testId={`cascade-grid-${round.index}`}
        renderCell={(reel, row, symbol) => (
          <CascadeCell
            key={`${reel}-${row}`}
            symbol={symbol}
            removed={containsCell(round.removed, reel, row)}
            dropped={containsCell(round.dropped, reel, row)}
            reel={reel}
            row={row}
            roundIndex={round.index}
          />
        )}
      />
    </div>
  );
}

function CascadeCell({
  symbol,
  removed,
  dropped,
  reel,
  row,
  roundIndex,
}: {
  symbol: string;
  removed: boolean;
  dropped: boolean;
  reel: number;
  row: number;
  roundIndex: number;
}) {
  // A cell can be BOTH dropped (refilled into this round) and removed (it
  // then won and vacates) — the background/border keeps the removed
  // styling priority (stacking rose + cyan tailwind classes would produce
  // a nondeterministic CSS conflict), but both corner icons render so the
  // refill signal isn't lost when an arriving symbol immediately wins.
  const classes = [
    "relative flex h-12 items-center justify-center border text-xs font-bold",
    removed
      ? "border-2 border-rose-600 bg-rose-500/15 text-rose-800 dark:border-rose-400 dark:text-rose-300"
      : dropped
        ? "border-2 border-cyan-600 bg-cyan-500/10 text-cyan-800 dark:border-cyan-400 dark:text-cyan-300"
        : "border-paper-border text-paper-text dark:border-term-border dark:text-term-text",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      data-testid={`cascade-cell-${roundIndex}-${reel}-${row}`}
      data-removed={removed ? "true" : undefined}
      data-dropped={dropped ? "true" : undefined}
      className={classes}
    >
      {removed && (
        <span aria-hidden className="absolute left-0.5 top-0.5 text-[9px]">
          💥
        </span>
      )}
      {dropped && (
        <span aria-hidden className="absolute right-0.5 top-0.5 text-[9px]">
          ✦
        </span>
      )}
      <span className="truncate px-1">{symbol}</span>
    </div>
  );
}

function TumbleConnector() {
  return (
    <div
      data-testid="cascade-connector"
      aria-hidden
      className="py-0.5 text-center text-[11px] text-paper-hint dark:text-term-hint"
    >
      ↓ tumble
    </div>
  );
}

function containsCell(
  positions: Array<[number, number]>,
  reel: number,
  row: number,
): boolean {
  return positions.some(([r, w]) => r === reel && w === row);
}
